const clinic = {
  name: 'NutriAxis Nutrition Consultancy & Wellness Clinic',
  city: 'Thika, Kenya',
  phone: '+254 117 478271',
  email: 'info.nutriaxis@gmail.com',
  pin: 'P052431364W',
  tagline: 'Nutrition. Balance. Wellness.'
};

const formatMoney = (value) => Number(value || 0).toLocaleString();

const getItemName = (item) => item.product_name || item.service_name || item.name || 'Item';

const getItems = (items = []) => items.map((item) => {
  const quantity = Number(item.quantity || 1);
  const total = Number(item.total_price || 0);
  const unitPrice = Number(item.unit_price || (quantity ? total / quantity : total));

  // For thermal: single line per item with left-aligned name and right-aligned totals
  const namePadded = (getItemName(item) || '').padEnd(20, ' ').substring(0, 20);
  const qtyStr = String(quantity).padStart(3, ' ');
  const priceStr = formatMoney(unitPrice).padStart(8, ' ');
  const totalStr = formatMoney(total).padStart(8, ' ');

  return `${namePadded}${qtyStr} ${priceStr} ${totalStr}`;
}).join('\n');

export const buildThermalReceipt = (saleData) => {
  const receiptNumber = saleData.receipt_number || 'N/A';
  const saleDate = new Date(saleData.sale_date || Date.now());
  const clientName = saleData.client_name || saleData.customer_name || 'Walk-in Customer';
  const total = Number(saleData.total_amount || 0);
  const discount = Number(saleData.discount || 0);
  const subtotal = Number(saleData.subtotal || total);
  const tax = Number(saleData.tax || 0);
  const amountPaid = Number(saleData.amount_paid || total);
  const change = Math.max(0, amountPaid - total);
  const cashierName = saleData.cashier_name || 'Staff';

  const isPartial =
    String(saleData.payment_status || '').toLowerCase().includes('partial') ||
    saleData.balance_due > 0;
  const remainingAmount = Number(saleData.balance_due || 0);

  const dateStr = `${saleDate.toLocaleDateString()} ${saleDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  const paymentMethod = (saleData.payment_method || 'CASH').toUpperCase();
  const mpesaCode = saleData.mpesa_confirmation_code || null;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Receipt ${receiptNumber}</title>
      <style>
        @page {
          size: 80mm auto;
          margin: 0;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Courier New', 'Lucida Console', monospace;
          font-size: 12px;
          line-height: 1.3;
          width: 72mm;
          padding: 2mm 3mm;
          color: #000;
          background: #fff;
        }
        .center {
          text-align: center;
        }
        .header {
          margin-bottom: 3mm;
          padding-bottom: 2mm;
          border-bottom: 1px dashed #000;
        }
        .header .clinic-name {
          font-size: 14px;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .header .clinic-info {
          font-size: 10px;
        }
        .header .tagline {
          font-size: 10px;
          font-style: italic;
          margin-top: 1mm;
        }
        .divider {
          border-top: 1px dashed #000;
          margin: 2mm 0;
        }
        .receipt-title {
          text-align: center;
          font-weight: bold;
          font-size: 14px;
          letter-spacing: 2px;
          margin: 2mm 0;
        }
        .meta {
          font-size: 11px;
          margin-bottom: 2mm;
        }
        .meta-row {
          display: flex;
          justify-content: space-between;
        }
        .items-header {
          font-weight: bold;
          border-bottom: 1px solid #000;
          border-top: 1px solid #000;
          padding: 1mm 0;
          margin-top: 2mm;
          font-size: 11px;
          display: flex;
        }
        .items-header span:first-child { flex: 1; }
        .items-header span:last-child { 
          text-align: right; 
          min-width: 70px;
        }
        .item-line {
          font-size: 11px;
          padding: 0.5mm 0;
          border-bottom: 1px dotted #ccc;
        }
        .item-name {
          font-weight: bold;
        }
        .item-details {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
        }
        .totals {
          margin-top: 2mm;
          font-size: 11px;
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 0.5mm 0;
        }
        .total-row.grand {
          font-weight: bold;
          font-size: 14px;
          border-top: 1px double #000;
          border-bottom: 1px double #000;
          padding: 1.5mm 0;
          margin: 1mm 0;
        }
        .payment-info {
          margin-top: 2mm;
          padding-top: 2mm;
          border-top: 1px dashed #000;
          font-size: 11px;
        }
        .payment-row {
          display: flex;
          justify-content: space-between;
        }
        .footer {
          text-align: center;
          margin-top: 3mm;
          padding-top: 2mm;
          border-top: 1px dashed #000;
          font-size: 11px;
        }
        .footer .thanks {
          font-weight: bold;
          font-size: 13px;
          margin-bottom: 1mm;
        }
        .barcode {
          text-align: center;
          font-family: 'Courier New', monospace;
          font-size: 18px;
          letter-spacing: 2px;
          margin: 2mm 0;
          color: #000;
        }
        .barcode-text {
          text-align: center;
          font-size: 10px;
          margin-top: -1mm;
        }
        @media print {
          body { width: 72mm; padding: 2mm 3mm; }
        }
      </style>
    </head>
    <body>
      <!-- Header -->
      <div class="header center">
        <div class="clinic-name">NutriAxis</div>
        <div class="clinic-info">${clinic.name}</div>
        <div class="clinic-info">${clinic.city}</div>
        <div class="clinic-info">${clinic.phone} | ${clinic.email}</div>
        <div class="clinic-info">PIN: ${clinic.pin}</div>
        <div class="tagline">${clinic.tagline}</div>
      </div>

      <div class="receipt-title">SALE RECEIPT</div>

      <!-- Meta -->
      <div class="meta">
        <div class="meta-row"><span>Receipt:</span><span>${receiptNumber}</span></div>
        <div class="meta-row"><span>Date:</span><span>${dateStr}</span></div>
        <div class="meta-row"><span>Cashier:</span><span>${cashierName}</span></div>
        <div class="meta-row"><span>Client:</span><span>${clientName}</span></div>
      </div>

      <div class="divider"></div>

      <!-- Items -->
      <div class="items-header">
        <span>Item</span>
        <span>Qty  Price   Total</span>
      </div>
      ${(saleData.items || []).map(item => {
        const qty = Number(item.quantity || 1);
        const totalPrice = Number(item.total_price || 0);
        const unitPrice = Number(item.unit_price || (qty ? totalPrice / qty : totalPrice));
        const name = getItemName(item);
        return `
          <div class="item-line">
            <div class="item-name">${name}</div>
            <div class="item-details">
              <span>${qty} × KES ${formatMoney(unitPrice)}</span>
              <span>KES ${formatMoney(totalPrice)}</span>
            </div>
          </div>
        `;
      }).join('\n')}

      <div class="divider"></div>

      <!-- Totals -->
      <div class="totals">
        <div class="total-row"><span>Subtotal</span><span>KES ${formatMoney(subtotal)}</span></div>
        ${discount > 0 ? `<div class="total-row"><span>Discount</span><span>-KES ${formatMoney(discount)}</span></div>` : ''}
        ${tax > 0 ? `<div class="total-row"><span>Tax</span><span>KES ${formatMoney(tax)}</span></div>` : ''}
        <div class="total-row grand"><span>TOTAL</span><span>KES ${formatMoney(total)}</span></div>
      </div>

      <!-- Payment Info -->
      <div class="payment-info">
        <div class="payment-row"><span>Payment Method:</span><span>${paymentMethod}</span></div>
        ${mpesaCode ? `<div class="payment-row"><span>M-Pesa Code:</span><span>${mpesaCode}</span></div>` : ''}
        <div class="payment-row"><span>Amount Paid:</span><span>KES ${formatMoney(amountPaid)}</span></div>
        ${change > 0 ? `<div class="payment-row"><span>Change:</span><span>KES ${formatMoney(change)}</span></div>` : ''}
        ${isPartial ? `
          <div class="payment-row" style="color:#b91c1c;font-weight:bold;margin-top:1mm;">
            <span>STATUS:</span><span>PARTIAL PAYMENT</span>
          </div>
          <div class="payment-row" style="color:#b91c1c;">
            <span>Balance Due:</span><span>KES ${formatMoney(remainingAmount)}</span>
          </div>
        ` : `
          <div class="payment-row" style="color:#15803d;font-weight:bold;margin-top:1mm;">
            <span>STATUS:</span><span>PAID IN FULL</span>
          </div>
        `}
      </div>

      <!-- Barcode style receipt number -->
      <div class="barcode">
        ||||||||||||||||||||||||||||||
      </div>
      <div class="barcode-text">${receiptNumber}</div>

      <!-- Footer -->
      <div class="footer">
        <div class="thanks">Thank you for choosing NutriAxis!</div>
        <div>${clinic.tagline}</div>
        <div style="margin-top:1mm;font-size:9px;">Goods once sold cannot be returned</div>
      </div>
    </body>
    </html>
  `;
};